import { useState, useRef, useEffect, useLayoutEffect, type RefObject } from 'react'

/**
 * Safari-pages-style tab overview mechanics, shared by the editor tab bar
 * and the agent session tabs.
 *
 * The strip content is ALWAYS flex-wrapped, so every tab has a fixed (x, y)
 * in the wrapped grid. Collapsed, the mask container is clamped to one row
 * and the strip is translated up so the ACTIVE tab's row shows; expanded,
 * the mask grows to reveal all rows as an overlay sheet. Because the tabs
 * never re-layout between the two states, their x positions are stable by
 * construction — the whole feature is a mask + transform, not a reflow.
 *
 * Geometry contract (must match the strip's CSS): a wrapped row's pitch
 * (pill height + row-gap) must equal `stripHeight` (padTop + pill +
 * padBottom), i.e. row-gap === padTop + padBottom. Then collapsing to
 * `stripHeight` shows exactly one row with clean padding and no neighbor
 * peek. `stripPad` is that padTop.
 *
 * Consumers render:
 *   <div style={{ position: relative, height: stripHeight }}>          // placeholder — owns the layout slot
 *     {open && <div className="ws-tab-scrim" onClick={close} />}
 *     <div ref={rowRef} className={`ws-tabrow ${open && 'ws-tabrow-open'}`} style={{ height: maskHeight }}>
 *       <div ref={stripRef} className="ws-tabbar…" style={{ transform: stripTransform }}>{pills}</div>
 *       {stickyActions}  // pages button etc.
 *     </div>
 *   </div>
 * Pills must carry `data-tab-id` so the active row can be located.
 */
export interface TabOverview {
  open: boolean
  setOpen: (open: boolean) => void
  toggle: () => void
  rowRef: RefObject<HTMLDivElement | null>
  stripRef: RefObject<HTMLDivElement | null>
  /** Height for the mask container (.ws-tabrow) */
  maskHeight: number
  /** Transform for the strip (.ws-tabbar) */
  stripTransform: string
  /** True when the tabs overflow onto more than one wrapped row */
  wrapped: boolean
}

export function useTabOverview({
  activeId,
  measureKey,
  stripHeight,
  stripPad,
  onOpenChange,
}: {
  /** Currently-active tab id (matched against pills' data-tab-id) */
  activeId: string | null
  /**
   * Change to force a re-measure. Must reflect anything that moves the
   * active tab to a different wrapped row: not just the tab count and active
   * id, but the tab ORDER and TITLES too (a reorder or rename re-wraps rows
   * without changing the count, and won't change strip scrollHeight, so the
   * ResizeObserver won't catch it either).
   */
  measureKey: unknown
  /** Collapsed one-row height = padTop + pill + padBottom */
  stripHeight: number
  /** Strip's top padding (padTop) */
  stripPad: number
  /**
   * Fired whenever the open state changes — including internal closes (Esc).
   * Consumers that mirror the open state (e.g. to lift a card's z-index)
   * must use this rather than wrapping setOpen, or Esc-close desyncs them.
   */
  onOpenChange?: (open: boolean) => void
}): TabOverview {
  const [open, setOpenState] = useState(false)
  const [rowOffset, setRowOffset] = useState(0)
  const [contentH, setContentH] = useState(stripHeight)
  const [availH, setAvailH] = useState(Number.MAX_SAFE_INTEGER)
  const rowRef = useRef<HTMLDivElement>(null)
  const stripRef = useRef<HTMLDivElement>(null)
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange
  // Tracks the current open value so setOpen can fire onOpenChange exactly
  // once per real transition, without a side effect inside the state updater
  // (which StrictMode double-invokes). setOpenState is only ever called here.
  const openRef = useRef(open)

  const setOpen = (o: boolean) => {
    if (openRef.current !== o) {
      openRef.current = o
      onOpenChangeRef.current?.(o)
    }
    setOpenState(o)
    // A native scroll left over from an over-cap sheet must not stack with
    // the collapsed mask's translateY.
    if (!o && rowRef.current) rowRef.current.scrollTop = 0
  }

  // Esc closes the sheet
  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [open])

  // Measure the wrapped grid: full content height, the room available to
  // grow downward (row top → card bottom, so it works even when the strip
  // isn't at the card's top), and the active tab's row offset. Transforms
  // don't affect offsetTop / layout rects, so measuring is safe in either
  // state.
  const measure = () => {
    const strip = stripRef.current
    const row = rowRef.current
    if (!strip || !row) return
    setContentH(Math.max(stripHeight, strip.scrollHeight))
    const card = row.closest('.ws-card, .ws-card-hero')
    if (card) {
      const cardRect = card.getBoundingClientRect()
      const rowRect = row.getBoundingClientRect()
      setAvailH(Math.max(stripHeight, cardRect.bottom - rowRect.top))
    }
    const active = activeId
      ? strip.querySelector<HTMLElement>(`[data-tab-id="${CSS.escape(activeId)}"]`)
      : null
    setRowOffset(active ? Math.max(0, active.offsetTop - stripPad) : 0)
  }
  const measureRef = useRef(measure)
  measureRef.current = measure
  useLayoutEffect(() => { measureRef.current() }, [measureKey])
  useEffect(() => {
    const strip = stripRef.current
    if (!strip) return
    const ro = new ResizeObserver(() => measureRef.current())
    ro.observe(strip)
    // Card height changes (window / panel resize) re-cap the open sheet.
    const card = rowRef.current?.closest('.ws-card, .ws-card-hero')
    if (card) ro.observe(card)
    return () => ro.disconnect()
  }, [])

  return {
    open,
    setOpen,
    toggle: () => setOpen(!open),
    rowRef,
    stripRef,
    // +1: mask height is border-box and .ws-tabrow has a 1px border-bottom;
    // without it the content area is 1px short and a scrollbar appears.
    maskHeight: open ? Math.min(contentH + 1, availH) : stripHeight,
    stripTransform: open ? 'translateY(0)' : `translateY(-${rowOffset}px)`,
    // A wrapped grid is taller than one row (small slack for sub-pixel rounding).
    wrapped: contentH > stripHeight + 1,
  }
}
